import Axios from "axios";
import React, { useState } from "react";
import { Button, LoadingDots } from "web-ui";

import { languageChallengeApi } from "../../api/languageChallenge";
import styles from "../../styles/GlobalChallengeAdmin.module.scss";

const XP_GOALS = [
  { id: "XP_0", label: "0 (Competition only)", value: 0 },
  { id: "XP_1000", label: "1,000 XP", value: 1000 },
  { id: "XP_2500", label: "2,500 XP", value: 2500 },
  { id: "XP_5000", label: "5,000 XP", value: 5000 },
  { id: "XP_7500", label: "7,500 XP", value: 7500 },
  { id: "XP_10000", label: "10,000 XP", value: 10000 },
];

const CreateChallengeForm = () => {
  const [challengeId, setChallengeId] = useState("");
  const [description, setDescription] = useState("");
  const [startTimestamp, setStartTimestamp] = useState("");
  const [endTimestamp, setEndTimestamp] = useState("");
  const [selectedXpGoal, setSelectedXpGoal] = useState("");
  const [streakEnabled, setStreakEnabled] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // Build objectiveIds array
      const objectiveIds: string[] = [];
      objectiveIds.push(selectedXpGoal);
      if (streakEnabled) {
        objectiveIds.push("STREAK_50");
      }

      await languageChallengeApi.createChallenge({
        challengeId,
        description,
        endTimestamp,
        objectiveIds,
        startTimestamp,
      });

      alert("Challenge created successfully!");
      // Reset form
      setChallengeId("");
      setDescription("");
      setStartTimestamp("");
      setEndTimestamp("");
      setSelectedXpGoal("");
      setStreakEnabled(false);
    } catch (error) {
      if (Axios.isAxiosError(error) && error.response) {
        const statusCode = error.response.status;
        const errorMessage =
          typeof error.response.data === "string"
            ? error.response.data
            : error.response.data
              ? JSON.stringify(error.response.data)
              : "Unknown error";
        alert(`Error ${statusCode}: ${errorMessage}`);
      } else {
        alert("An error has occurred. Please try again later.");
        throw error;
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid =
    challengeId &&
    description &&
    startTimestamp &&
    endTimestamp &&
    selectedXpGoal !== "";

  return (
    <div className={styles.section}>
      <h2 className={styles.sectionTitle}>Create New Challenge</h2>
      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.formGroup}>
          <label className={styles.label} htmlFor="challengeId">
            Challenge ID <span className={styles.required}>*</span>
          </label>
          <p className={styles.helpText}>
            Unique identifier (e.g., 2025-UK). Will be prefixed with EXT-
            automatically.
          </p>
          <input
            className={styles.input}
            id="challengeId"
            onChange={e => setChallengeId(e.target.value)}
            type="text"
            value={challengeId}
          />
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label} htmlFor="description">
            Description <span className={styles.required}>*</span>
          </label>
          <input
            className={styles.input}
            id="description"
            onChange={e => setDescription(e.target.value)}
            type="text"
            value={description}
          />
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label} htmlFor="startTimestamp">
            Start Timestamp (UTC) <span className={styles.required}>*</span>
          </label>
          <p className={styles.helpText}>
            Format: YYYY-MM-DDTHH:MM:SSZ (e.g., 2025-01-01T00:00:00Z)
          </p>
          <input
            className={styles.input}
            id="startTimestamp"
            onChange={e => setStartTimestamp(e.target.value)}
            type="text"
            value={startTimestamp}
          />
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label} htmlFor="endTimestamp">
            End Timestamp (UTC) <span className={styles.required}>*</span>
          </label>
          <p className={styles.helpText}>
            Format: YYYY-MM-DDTHH:MM:SSZ (e.g., 2025-04-14T23:59:59Z)
          </p>
          <input
            className={styles.input}
            id="endTimestamp"
            onChange={e => setEndTimestamp(e.target.value)}
            type="text"
            value={endTimestamp}
          />
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>
            XP Goal <span className={styles.required}>*</span>
          </label>
          <div className={styles.radioGroup}>
            {XP_GOALS.map(goal => (
              <label className={styles.radioLabel} key={goal.id}>
                <input
                  checked={selectedXpGoal === goal.id}
                  className={styles.radio}
                  name="xpGoal"
                  onChange={() => setSelectedXpGoal(goal.id)}
                  type="radio"
                  value={goal.id}
                />
                <span>{goal.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>Streak Goal</label>
          <div className={styles.checkboxGroup}>
            <label className={styles.checkboxLabel}>
              <input
                checked={streakEnabled}
                className={styles.checkbox}
                onChange={e => setStreakEnabled(e.target.checked)}
                type="checkbox"
              />
              <span>50-day streak (optional)</span>
            </label>
          </div>
        </div>

        <Button
          className={styles.submitButton}
          color="macaw"
          disabled={!isFormValid || isSubmitting}
          type="submit"
          variant="solid"
        >
          {isSubmitting ? <LoadingDots type="button" /> : "Create Challenge"}
        </Button>
      </form>
    </div>
  );
};

export default CreateChallengeForm;
